<script setup lang="ts">
import {computed, nextTick} from "vue";
import { useTranslate } from "@tolgee/vue";
import { RequisiteAddressType } from "@enums/RequisiteAddressType";
import ModelAddressType from "@models/v2/addressType/ModelAddressType";
import Requisite from "@models/requisite/Requisite";

interface Props {
	requisite: Requisite | null;
	addressTypes: ModelAddressType[];
}

interface Emits {
	(e: 'select', value: { id: number | null; slug: RequisiteAddressType }): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Emits>();
const { t } = useTranslate();

const values = computed(() => {
	const base = [
		{ slug: RequisiteAddressType.CARD, title: t.value('requisite.address-types.card') },
		{ slug: RequisiteAddressType.PHONE, title: t.value('requisite.address-types.phone') }
	];

	let firstAvailableSet = false;

	return base.map(item => {
		const addressType = props.addressTypes.find(a => a.slug === item.slug);

		const disabled =
			!addressType ||
			!props.requisite?.psystem ||
			!props.requisite.psystem?.addressTypes?.some(id => id === addressType?.id);

		const addressTypeId = props.requisite?.addressTypeId ?? null;

		const active = addressTypeId
			? addressTypeId === addressType?.id
			: (!disabled && !firstAvailableSet && (firstAvailableSet = true));

		return {
			...item,
			id: addressType?.id ?? null,
			disabled,
			active
		};
	});
});

const handleSelect = (value: typeof values.value[number]) => {
	if (value.disabled) return;

	emits('select', {
		id: value.id,
		slug: value.slug
	});
};

nextTick(() => {
	const firstActive = values.value.find(v => v.active);
	if (firstActive && !props.requisite?.addressTypeId) {
		emits('select', {
			id: firstActive.id,
			slug: firstActive.slug
		});
	}
});
</script>

<template>
	<div class="requisite-address-type-toggle">
		<div
			v-for="value in values"
			:key="value.slug"
			:class="[
				'requisite-address-type-toggle__item',
				{ active: value.active, disabled: value.disabled }
			]"
			@click="handleSelect(value)"
		>
			{{ value.title }}
		</div>
	</div>
</template>

<style scoped lang="scss">
.requisite-address-type-toggle {
	display: flex;
	align-items: stretch;
	margin: 12px 0;
	gap: 12px;

	padding-left: calc(var(--f7-block-padding-horizontal) + var(--f7-safe-area-left));
	padding-right: calc(var(--f7-block-padding-horizontal) + var(--f7-safe-area-right));

	&__item {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: var(--white1);
		flex: 1;
		padding: 8px;
		text-align: center;
		font-weight: 500;
		border: 1px solid var(--grey8);
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s ease;

		&.active {
			border-color: var(--blue1);
		}

		&.disabled {
			cursor: default;
			opacity: 0.55;
			pointer-events: none;
		}
	}
}
</style>
